/**
 * 支付页面**上架商品集合 + 展示顺序**契约 schema —— dev-docs `02-API契约/A8-运营支付.md` §7.9
 * （ADM-PAY-S009；表结构见 `03-数据字典/70-产品配置.md` §3.10）。
 *
 * **本端点写入的既是集合也是顺序**（dd#1042 起）：`access_level_ids` 里有 = 该页上架，数组位置
 * 即 `sort_order`；**不在数组里 = 该页不上架**。一条行 = 一个上架商品，故 §3.10 把删行定义成
 * 「该商品在该页下架」。
 *
 * **整组替换**，与 §7.8.2 通道策略同体例：单条改位次在并发下产生重复值与空洞，
 * 也无法原子表达「把 B 插到 A 前面」。
 *
 * 三点与直觉相反、由契约明写、实现方不得自行收紧：
 *   - **允许提交候选集的子集** —— 未列出的产品是「本页不上架」，不是错误，也不从候选列表里消失
 *     （运营要能看到它、才能把它加回来）；
 *   - **空数组 = 删光本页全部行 = 回落展示全集**，不是「本页不卖任何商品」。要整页不卖走下架页
 *     （ADM-PAY-S002），判据见 §3.10 回落规则第 ③ 级；
 *   - **不校验可售状态、不校验板块归属** —— 运营可先配置后上架；板块先后由服务端排序键固定承载，
 *     提交数组里板块交错不影响结果。
 */

import { z } from 'zod';

/**
 * 有序 `access_level_id` 数组：**数组位置即 `sort_order`**（服务端按下标 0..n-1 写入），
 * 故本 schema 不接收 `sort_order` 入参。
 *
 * Zod 只保证「格式合法 + 组内不重复」；**「该 App 确有此 access_level_id 且未软删」是跨表事实，
 * 由 service 层校验**（§7.9：无效项 → VALIDATION_FAILED 且 details 列出）。表层无 FK 兜底
 * （§3.10 刻意不设），所以那道校验是唯一防线，不能因为这里已经过了 Zod 就省掉。
 */
export const PaymentPageProductOrderIdsSchema = z
  .array(z.string().min(1).max(255))
  .refine((ids) => new Set(ids).size === ids.length, {
    message: 'access_level_ids must not contain duplicate values',
  });

/**
 * PUT /api/v1/payment-pages/:id/product-order 请求体（§7.9）。
 * **strict**：请求体字段闭集 = `access_level_ids` 一键，额外字段 → VALIDATION_FAILED（typo 不静默 strip）。
 */
export const ReplacePaymentPageProductOrderRequestSchema = z
  .object({
    access_level_ids: PaymentPageProductOrderIdsSchema,
  })
  .strict();

export type ReplacePaymentPageProductOrderRequest = z.infer<
  typeof ReplacePaymentPageProductOrderRequestSchema
>;

/** 产品板块归属（§7.9 `items[].product_type`）：板块先后固定不可配，SUBSCRIPTION 在前。 */
export type PaymentPageProductOrderProductType = 'CONSUMABLE' | 'SUBSCRIPTION';

/** GET / PUT 响应的候选产品条目（§7.9 字段表）。 */
export interface PaymentPageProductOrderItem {
  access_level_id: string;
  /** 运营识别名（源 product_metadata.name） */
  name: string;
  /** 多语言展示标题（源 product_metadata.title_i18n） */
  title_i18n: Record<string, unknown> | null;
  /** 板块归属；前端据此分块渲染，板块先后不可配（CLI-RCG-001：SUBSCRIPTION 在前） */
  product_type: PaymentPageProductOrderProductType;
  /**
   * **本页上架状态 + 位次的合一表达**（§7.9）：非 null = 该商品在本页上架且位次为该值；
   * `null` = 该商品不在本页上架集合内。
   *
   * `is_configured=false`（整页无配置行）时本字段恒 null，C 端该页空态（§3.10「无配置即空集」）。
   */
  sort_order: number | null;
  /**
   * 该逻辑产品是否已可进 C 端 serving。§1 的 `is_serveable` 是**通道行级**（IAP / STRIPE 各一行），
   * 本字段按**任一通道行通过即 true** 聚合，与 C 端「该 access level 至少有一个可售通道 key
   * 才展示」的口径一致；供运营识别「已上架但暂不可售」的行。
   */
  is_serveable: boolean;
  /** 发现面归属独立于可售状态；不属于本页仍允许保存。 */
  is_in_discovery_scope: boolean;
}

/** GET / PUT 响应体（§7.9：PUT 返回结构同 GET —— 替换后的完整候选列表 + 新集合与次序）。 */
export interface PaymentPageProductOrderResponse {
  /** 该页 scenario 回显 */
  scenario: string;
  /**
   * **该页有没有配置行**（§7.9）。`false` = 一行都没有 = C 端按 §3.10「无配置即空集」返回该页
   * **空态**；`true` = 已配集合，C 端只展示 `sort_order` 非 null 的那些。
   *
   * **前端必须据本字段分文案**（契约原文）：「尚未配置」与「配置行全指向已软删商品」在 items[] 上
   * 同形（`sort_order` 全为 null），只有本字段能区分。
   *
   * **买断页与运营弹窗页不读本表**（§3.10「适用性判据」），本字段对这两类页只表示有无配置行，
   * `true` / `false` 都不改变 C 端展示。
   */
  is_configured: boolean;
  /**
   * **本页上架集合的逻辑产品数**（纯集合口径，§7.9）：`is_configured=false` → `0`；
   * `true` → `sort_order` 非 null 的条数。
   *
   * **不等于 C 端实际展示数** —— serving 侧还会按发现面 scenario 归属与 `is_serveable` 再过滤，
   * 故在适用页面上本值恒 ≥ 实际展示数。**本响应不提供 C 端实际可见数、也无法由 `items[]` 求出**
   * （`items[]` 不含发现面 scenario 归属），admin 刻意不复刻 serving 侧求交。
   */
  effective_product_count: number;
  items: PaymentPageProductOrderItem[];
}

/**
 * 活动集合与两级取集合来源（§6.7）：`NONE` = 活动与 scenario 都无行，C 端该活动页空态、
 * `effective_access_level_ids` 为 `[]`。编辑时只使用本活动 sort_order。
 */
export interface PaymentOfferProductOrderResponse extends PaymentPageProductOrderResponse {
  offer_id: string;
  effective_source: 'OFFER' | 'SCENARIO' | 'NONE';
  effective_access_level_ids: string[];
}
